import { useCallback, useContext, useEffect, useRef } from 'react'
import _ from 'lodash'
import { RoomContext, RoomContextState, RoomContextType } from '../RoomContext'
import { logger } from './Logger'
import { LiveKitMetadata, PeerManifest } from './RoomContextMusePeer'
import useRoomActions from './useRoomActions'
import useManifestMethods from './useManifestMethods'
import useRoomContextRef from './useRoomContextRef'
import { ConnectionState, Room } from 'livekit-client'
import useUserContextRef from './useUserContextRef'
import LiveKitManifestSupport from './LiveKitManifestSupport'
import { UserContext, UserContextType } from '../UserContext'
import { useVideoOrder } from '@/pages/SessionPage/internal/shared/useViewMode'

const log = logger.createChildLogger({ name: 'useManifestService' })

export default function useLocalManifestTransmitter() {
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const videoOrder = useVideoOrder()

    const { roomStateRef } = useRoomContextRef()
    const { userStateRef } = useUserContextRef()

    const { generateP2pManifest, generateSfuManifest, generateLegacySfuManifest } = useManifestMethods()

    const { sendMessageToPeer } = useRoomActions()

    let sendManifestThroughChannelTimerRef = useRef<{ [key: string]: NodeJS.Timeout }>({})
    const sendManifestThroughChannel = (sessionId: string, manifest: PeerManifest) => {
        Object.keys(sendManifestThroughChannelTimerRef.current).forEach((sessionId) => {
            clearTimeout(sendManifestThroughChannelTimerRef.current[sessionId])
        })

        sendMessageToPeer(sessionId, {
            type: 'peer-manifest-broadcast',
            data: manifest,
        })

        sendManifestThroughChannelTimerRef.current[sessionId] = setTimeout(() => {
            sendMessageToPeer(sessionId, {
                type: 'peer-manifest-broadcast',
                data: manifest,
            })

            sendManifestThroughChannelTimerRef.current[sessionId] = setTimeout(() => {
                sendMessageToPeer(sessionId, {
                    type: 'peer-manifest-broadcast',
                    data: manifest,
                })
            }, 6000)
        }, 3000)
    }

    const setLivekitMetadata = (sfuRoom: Room, metadata: LiveKitMetadata) => {
        try {
            const m = JSON.stringify(metadata)
            log.silly('setLivekitMetadata', `set livekit metadata, size=${m.length}.`)
            sfuRoom.localParticipant.setMetadata(m)
        } catch (e) {
            log.error('setLivekitMetadata', `Failed to set Livekit metadata. ` + `(sfuRoom.state=${sfuRoom.state}).`, {
                e,
            })
        }
    }

    // Detect when it is necessary to create a PeerManifest and send it.
    const prevPerPeerManifestRef = useRef<{ [key: string]: PeerManifest }>({})
    const prevMetadataRef = useRef<LiveKitMetadata | null>()

    const clearPrevCache = useCallback(() => {
        if (prevPerPeerManifestRef.current) {
            Object.keys(prevPerPeerManifestRef.current).forEach((k) => {
                delete prevPerPeerManifestRef.current[k]
            })
        }
        prevMetadataRef.current = null
    }, [])

    const generateAndSendManifestP2p = (roomState: RoomContextState) => {
        Object.keys(roomState.peers).forEach((sessionId) => {
            const peer = roomState.peers[sessionId]
            if (!peer.p2pConnection) {
                log.verbose(
                    'generateAndSendManifestEffect',
                    `(sessionId="${sessionId}") not ` +
                        `sending manifest because peer's p2pConnection has not loaded yet.`
                )
                return
            }

            const manifest = generateP2pManifest(peer)

            if (!_.isEqual(prevPerPeerManifestRef.current[sessionId], manifest)) {
                log.verbose(
                    `generateAndSendManifestEffect`,
                    `(sessionId="${sessionId}") generated ` + `manifest; it is different so sending it.`
                )

                sendManifestThroughChannel(sessionId, manifest)

                prevPerPeerManifestRef.current[sessionId] = manifest
            } else {
                log.verbose(
                    `generateAndSendManifestEffect`,
                    `(sessionId="${sessionId}") generated ` + `manifest but it was the same as before. no-op.`
                )
            }
        })
    }

    const generateAndSendManifestSfu = useCallback((roomState: RoomContextState) => {
        const room = roomStateRef.current.sfuRoom
        if (!room || room.state !== ConnectionState.Connected) {
            log.verbose('generateAndSendManifestEffect', 'sfuRoom is not connected; no-op.')
            return
        }

        let metadata: LiveKitMetadata = {
            sessionId: userStateRef.current.sessionId,
            userUrn: userStateRef.current.userUrn,
            manifest: generateSfuManifest(),
        }

        Object.keys(roomState.peers).forEach((sessionId) => {
            const peer = roomState.peers[sessionId]

            if (!LiveKitManifestSupport.isSupported(peer.sessionId)) {
                const legacyManifest = generateLegacySfuManifest(peer)
                if (!_.isEqual(prevPerPeerManifestRef.current[sessionId], legacyManifest)) {
                    log.verbose(
                        `generateAndSendManifestEffect`,
                        `(sessionId="${sessionId}") generated ` + `manifest; it is different so sending it.`
                    )

                    sendManifestThroughChannel(sessionId, legacyManifest)

                    prevPerPeerManifestRef.current[sessionId] = legacyManifest
                } else {
                    log.verbose(
                        `generateAndSendManifestEffect`,
                        `(sessionId="${sessionId}") generated ` + `manifest but it was the same as before. no-op.`
                    )
                }
            }
        })

        if (room && room.state === ConnectionState.Connected) {
            if (!_.isEqual(prevMetadataRef.current, metadata)) {
                log.isVerbose() && log.verbose('generateAndSendManifestEffect', 'setting metadata', { metadata })
                prevMetadataRef.current = metadata
                setLivekitMetadata(room, metadata)
            }
        }
    }, [])

    const generateAndSendManifest = useCallback((roomState: RoomContextState) => {
        if (roomState.expectedNetworkTopology === 'p2p') {
            generateAndSendManifestP2p(roomState)
        } else {
            generateAndSendManifestSfu(roomState)
        }
    }, [])

    useEffect(
        function generateAndSendManifestEffect() {
            generateAndSendManifest(roomState)
        },
        [roomState, state.mediaStreams, videoOrder]
    )

    // in-place roomUrn changes may occur when switching between subrooms.
    useEffect(() => {
        // when the dependencies for this effect get changed, then clear the cache
        //a nd then run generateAndSendManifest().
        clearPrevCache()
        generateAndSendManifest(roomStateRef.current)
    }, [roomState.sfuRoomConnected, roomState.expectedNetworkTopology])

    return {}
}
